import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./ApproveVendors.css";
import Pagination from "../ShipturtleProducts/Pagination";
import { showToast } from "../../utils/toast";
import { VendorDetailView } from "./ManageVendors";
import DataFallback from '../feedback/DataFallback';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/shipturtle/vendors`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

function ApproveVendors() {
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemsPerPage] = useState(25);
  const [serverPagination, setServerPagination] = useState(null);
  const [approvingVendorId, setApprovingVendorId] = useState(null); // Track which vendor is being approved

  // ✅ Fetch only PENDING vendors from backend with server-side pagination
  const fetchPendingVendors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: itemsPerPage.toString(),
        status: "pending",
      });

      // Add search term if provided
      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const response = await fetch(`${API_BASE_URL}/?${params.toString()}`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError("Authentication failed. Please log in again.");
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.detail || errorData.message || response.statusText;
        setError(`Failed to load vendors (${response.status}): ${message}`);
        return;
      }

      const data = await response.json();

      let vendorsData = [];
      if (data) {
        if (Array.isArray(data)) {
          vendorsData = data;
        } else if (Array.isArray(data.results)) {
          vendorsData = data.results;
        } else if (Array.isArray(data.data)) {
          vendorsData = data.data;
        }
      }

      setVendors(vendorsData);
      
      // Set pagination metadata if available
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        if (data.count !== undefined || data.total_pages !== undefined) {
          setServerPagination({
            total_count: data.count || vendorsData.length,
            total_pages: data.total_pages || Math.ceil((data.count || vendorsData.length) / itemsPerPage),
            current_page: data.current_page || currentPage,
            page_size: itemsPerPage,
            has_next: data.has_next || false,
            has_previous: data.has_previous || false,
          });
        } else {
          setServerPagination(null);
        }
      } else {
        setServerPagination(null);
      }
    } catch (err) {
      console.error("Error fetching pending vendors:", err);
      setError(`Failed to fetch pending vendors: ${err.message || 'Check API.'}`);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    fetchPendingVendors();
  }, [fetchPendingVendors]);

  // Calculate pagination values
  const { totalPages, startIndex, endIndex, totalCount } = useMemo(() => {
    if (serverPagination) {
      const pageSize = Number(serverPagination.page_size) || itemsPerPage;
      const totalPagesCalc = Number(serverPagination.total_pages) || 1;
      const start = (currentPage - 1) * pageSize;
      const end = start + (vendors ? vendors.length : 0);
      return {
        totalPages: totalPagesCalc,
        startIndex: start,
        endIndex: end,
        totalCount: Number(serverPagination.total_count) || (vendors ? vendors.length : 0)
      };
    }
    // Fallback to client-side calculation
    const total = vendors.length;
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return {
      totalPages: Math.ceil(total / itemsPerPage),
      startIndex: start,
      endIndex: Math.min(end, total),
      totalCount: total
    };
  }, [vendors, currentPage, itemsPerPage, serverPagination]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // State for edit vendor view
  const [showEditVendor, setShowEditVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  // Handle edit vendor - fetch details and show edit view
  const handleEditVendor = useCallback(async (vendor) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${vendor.id}/`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          showToast({ type: 'error', message: 'Authentication failed. Please log in again.' });
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setEditingVendor(data);
      setShowEditVendor(true);
    } catch (err) {
      console.error("Failed to load vendor details:", err);
      showToast({ type: 'error', message: `Failed to load vendor details: ${err.message || 'Check logs.'}` });
    }
  }, []);

  const handleApprove = async (vendorId) => {
    // Find the vendor to get its name for the toast message
    const vendor = vendors.find(v => v.id === vendorId);
    const vendorName = vendor?.company_name || vendor?.brand_name || 'Vendor';
    
    // Set loading state for this specific vendor
    setApprovingVendorId(vendorId);
    
    try {
      const response = await fetch(`${API_BASE_URL}/${vendorId}/approve/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        if (response.status === 401) {
          showToast({ type: 'error', message: 'Authentication failed. Please log in again.' });
          setApprovingVendorId(null);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      // Show success toast with vendor name and make it bigger
      showToast({ 
        type: 'success', 
        message: `"${vendorName}" has been approved successfully!`,
        duration: 5000, // Show for 5 seconds
        toastOptions: {
          style: {
            fontSize: '16px',
            padding: '16px 24px',
            minWidth: '300px',
          },
        }
      });
      fetchPendingVendors(); // ✅ Refresh pending list after approval
    } catch (error) {
      console.error("Error approving vendor:", error);
      showToast({ type: 'error', message: `Failed to approve vendor: ${error.message || "Check logs for details."}` });
    } finally {
      // Clear loading state
      setApprovingVendorId(null);
    }
  };

  // Show edit vendor view if editing
  if (showEditVendor && editingVendor) {
    return (
      <VendorDetailView 
        vendor={editingVendor}
        onClose={() => {
          setShowEditVendor(false);
          setEditingVendor(null);
          // Refresh the pending vendors list after closing edit view
          fetchPendingVendors();
        }}
      />
    );
  }

  return (
    <div className="approveVendorsContainer">
      <div className="vendorsWhiteBox">
        <div className="manageVendorsHeader">
          <div className="headerLeft">
            <h2>Approve Vendors</h2>
          </div>
        </div>

        {/* Search Bar */}
        <div className="searchFilterBar">
          <div className="searchInputWrapper">
            <input
              type="text"
              className="searchInput"
              placeholder="Search Pending Vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Results Summary */}
        {!loading && !error && vendors.length > 0 && (
          <div className="resultsSummary">
            <p>
              Showing {startIndex + 1}-{Math.min(endIndex, totalCount)} of {totalCount} pending vendors
            </p>
          </div>
        )}

        {loading ? (
          <div className="loadingContainer">
            <p>Loading pending vendors...</p>
          </div>
        ) : error ? (
          <DataFallback
            variant="table"
            error={typeof error === 'string' ? { message: error, retryable: true } : error}
            onRetry={() => fetchPendingVendors()}
          />
        ) : vendors.length > 0 ? (
          <>
            <div className="vendorTableWrapper">
              <table className="vendorTable">
                <thead>
                  <tr>
                    <th>Logo</th>
                    <th>Vendor Name</th>
                    <th>Status</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>City</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((vendor) => {
                    const vendorInitial = vendor.company_name?.charAt(0)?.toUpperCase() || '?';
                    return (
                      <tr key={vendor.id}>
                        <td>
                          <div className="logoCircle" style={{ 
                            background: vendor.logo_color || '#007b83',
                            color: 'white'
                          }}>
                            {vendorInitial}
                          </div>
                        </td>
                        <td>
                          <div 
                            className="vendorNameCell approveVendorName"
                            onClick={() => handleEditVendor(vendor)}
                            style={{ cursor: 'pointer' }}
                            title="Click to edit vendor"
                          >
                            <div className="vendorName">{vendor.company_name || vendor.brand_name}</div>
                            {vendor.brand_name && vendor.brand_name !== vendor.company_name}
                          </div>
                        </td>
                        <td>
                          <span className="pendingStatus">
                            {vendor.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td>{vendor.primary_contact_phone || "—"}</td>
                        <td>{vendor.email || vendor.primary_contact_email || "—"}</td>
                        <td>{vendor.address_city || "—"}</td>
                        <td>
                          <button
                            className="approveBtn"
                            onClick={() => handleApprove(vendor.id)}
                            disabled={approvingVendorId === vendor.id}
                          >
                            {approvingVendorId === vendor.id ? (
                              <>
                                <span className="approveBtnSpinner"></span>
                                Approving...
                              </>
                            ) : (
                              'Approve'
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <p className="noDataText">No pending vendors to approve.</p>
        )}
      </div>
    </div>
  );
}

export default ApproveVendors;
